import React, { useEffect, useState } from 'react';
import './App.css';

// Import components
// import NavigationBar from './components/NavigationBar';
import Footer from './components/Footer';
import HomeScreen from './components/screens/HomeScreen';
import ReadyScreen from './components/screens/ReadyScreen';
import CountdownScreen from './components/screens/CountdownScreen';
// Removed intermediate screens for simplified flow
import ReadingScreen from './components/screens/ReadingScreen';
import ResultsScreen from './components/screens/ResultsScreen';

const SCREENS = {
  HOME: 'home',
  READY: 'ready',
  COUNTDOWN: 'countdown',
  READING: 'reading',
  RESULTS: 'results'
};

function App() {
  const [currentScreen, setCurrentScreen] = useState(SCREENS.HOME);
  const [selectedGrade, setSelectedGrade] = useState(null); // 1..8 or 'HS'
  const [currentReadingGrade, setCurrentReadingGrade] = useState(null); // number or 'HS'
  const [results, setResults] = useState([]); // [{grade, accuracyAvg, wcpm, pass}]
  const [countdown, setCountdown] = useState(3);
  const [attemptsMap, setAttemptsMap] = useState({}); // { gradeKey: attemptCount }
  const [currentAttemptNumber, setCurrentAttemptNumber] = useState(1); // 1 or 2
  const [fallbackActive, setFallbackActive] = useState(false); // triggers internal Alphabet/Words in ReadingScreen
  const [preMessageText, setPreMessageText] = useState(null); // message shown before reading starts
  const [highestFailedGrade, setHighestFailedGrade] = useState(null); // track highest grade they've failed
  const [passedGrades, setPassedGrades] = useState(new Set()); // track which grades they've passed
  const [alphabetResults, setAlphabetResults] = useState(null); // store alphabet pretest results

  // starting reading grade is computed in handleStart

  const handleStart = (grade) => {
    setSelectedGrade(grade);
    // Compute starting reading grade (2 less, min 1)
    const n = grade === 'HS' ? 9 : parseInt(grade, 10);
    const s = Math.max(1, (isNaN(n) ? 1 : n) - 2);
    setCurrentReadingGrade(s >= 9 ? 'HS' : s);
    setResults([]);
    setAttemptsMap({});
    setCurrentAttemptNumber(1);
    setHighestFailedGrade(null);
    setPassedGrades(new Set());
    setAlphabetResults(null);
    setCurrentScreen(SCREENS.READY);
  };

  const handleReadyFinished = () => {
    setCountdown(3);
    setCurrentAttemptNumber(1);
    setCurrentScreen(SCREENS.COUNTDOWN);
  };

  const handleReadingComplete = async ({ pass, accuracyAvg, wcpm, grade, matched, total, passWords, finishReason }) => {
    const gradeNum = grade === 'HS' ? 9 : parseInt(grade, 10);
    const gradeKey = grade === 'HS' ? 'HS' : String(gradeNum);
    const currentAttemptsBefore = attemptsMap[gradeKey] || 0; // attempts completed before this result
    const thisAttemptNumber = currentAttemptsBefore + 1;
    console.log('[ReadingComplete] Received', { finishReason, pass, accuracyAvg, wcpm, grade, attempt: thisAttemptNumber, matched, total, passWords });
    setResults(prev => [...prev, { grade: gradeKey, accuracyAvg, wcpm, pass, attempt: thisAttemptNumber, matched, total, passWords, finishReason }]);
    setAttemptsMap(prev => ({ ...prev, [gradeKey]: thisAttemptNumber }));

    // Only proceed if the reading actually finished (completed or timeout)
    if (finishReason !== 'completed' && finishReason !== 'timeout') {
      console.log('[ReadingComplete] Not proceeding — finishReason is not terminal:', finishReason);
      return;
    }
    console.log('[ReadingComplete] Proceeding — finishReason is terminal:', finishReason);

    // Update currentAttemptNumber for next screen
    if (!pass && thisAttemptNumber < 2) {
      // Second attempt: ReadingScreen will show its own overlay/audio
      console.log('[Progression] Attempt failed but retries remain — retrying same grade', { grade: gradeKey, nextAttempt: thisAttemptNumber + 1 });
      setPreMessageText("try again");
      setCurrentAttemptNumber(thisAttemptNumber + 1);
      setCurrentScreen(SCREENS.READING);
      return;
    }

    // Continue to next grade until reaching HS (grade 9) or until they fail twice
    const isLastGrade = gradeNum >= 9; // HS is treated as 9
    if (pass) {
      // Track this grade as passed
      setPassedGrades(prev => new Set([...prev, gradeNum]));
      
      console.log('[Progression] Passed grade', { grade: gradeKey, isLastGrade, highestFailedGrade });
      
      // If they've previously failed a higher grade, stop them here
      if (highestFailedGrade !== null && gradeNum < highestFailedGrade) {
        console.log('[Progression] Passed grade lower than previously failed grade — showing RESULTS', { 
          currentGrade: gradeNum, 
          highestFailedGrade 
        });
        setCurrentScreen(SCREENS.RESULTS);
        return;
      }
      
      if (!isLastGrade) {
        const nextNum = Math.min(9, gradeNum + 1);
        const nextKey = nextNum >= 9 ? 'HS' : nextNum;
        console.log('[Progression] Advancing to next grade', { from: gradeKey, to: nextKey });
        setCurrentReadingGrade(nextKey);
        setCurrentAttemptNumber(1);
        setPreMessageText("Ready");
        console.log('[Navigation] Moving to READING for next grade');
        setCurrentScreen(SCREENS.READING);
      } else {
        console.log('[Progression] Last grade reached and passed — showing RESULTS');
        setCurrentScreen(SCREENS.RESULTS);
      }
      return;
    }

    if (!pass && thisAttemptNumber >= 2) {
      // Track this as the highest failed grade
      setHighestFailedGrade(prev => prev === null ? gradeNum : Math.max(prev, gradeNum));
      
      // Special case: if grade 1 failed twice, trigger fallback inside ReadingScreen
      if (gradeNum === 1) {
        console.log('[Progression] Double fail on Grade 1 — entering fallback flow');
        setCurrentAttemptNumber(1);
        setFallbackActive(true);
        setPreMessageText(null);
        console.log('[Navigation] Moving to READING (fallback inside ReadingScreen)');
        setCurrentScreen(SCREENS.READING);
      } else {
        // Find the highest grade they've passed that's lower than current grade
        const passedGradesArray = Array.from(passedGrades).sort((a, b) => b - a);
        const highestPassedBelow = passedGradesArray.find(grade => grade < gradeNum);
        
        if (highestPassedBelow !== undefined) {
          // They've already passed a grade below current, show results
          console.log('[Progression] Double fail — already passed grade below current, showing RESULTS', { 
            currentGrade: gradeNum,
            highestPassedBelow,
            passedGrades: Array.from(passedGrades)
          });
          setCurrentScreen(SCREENS.RESULTS);
        } else {
          // No passed grades below current grade, move down one level
          const prevNum = Math.max(1, gradeNum - 1);
          const prevKey = prevNum === 1 ? 1 : prevNum;
          console.log('[Progression] Double fail — moving down to previous grade', { 
            from: gradeKey, 
            to: prevKey, 
            highestFailedGrade: gradeNum,
            passedGrades: Array.from(passedGrades)
          });
          setCurrentReadingGrade(prevKey);
          setCurrentAttemptNumber(1);
          setPreMessageText("Ready");
          console.log('[Navigation] Moving to READING for previous grade');
          setCurrentScreen(SCREENS.READING);
        }
      }
    }
  };
  const handleFallbackDone = (alphabetResultsData) => {
    if (alphabetResultsData) {
      setAlphabetResults(alphabetResultsData);
    }
    setFallbackActive(false);
    setCurrentScreen(SCREENS.RESULTS);
  };

  // Countdown tick: when on countdown screen, decrement every 1s and then go to reading
  useEffect(() => {
    if (currentScreen !== SCREENS.COUNTDOWN) return;
    if (countdown <= 0) {
      setCurrentScreen(SCREENS.READING);
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1500);
    return () => clearTimeout(t);
  }, [currentScreen, countdown]);

  // Stop any ongoing recording/streaming when switching to RESULTS
  useEffect(() => {
    if (currentScreen === SCREENS.RESULTS) {
      try {
        if (typeof window !== 'undefined' && typeof window.suspendRecordingAudio === 'function') {
          window.suspendRecordingAudio(true);
        }
      } catch (e) {
        console.warn('[App] Failed to stop recording on RESULTS navigation:', e);
      }
    }
  }, [currentScreen]);

  const renderScreen = () => {
    switch (currentScreen) {
      case SCREENS.HOME:
        return <HomeScreen onNext={handleStart} />;
      case SCREENS.READY:
        return <ReadyScreen onNext={handleReadyFinished} />;
      case SCREENS.COUNTDOWN:
        return <CountdownScreen countdown={countdown} />;
      case SCREENS.READING:
        return (
          <ReadingScreen
            onComplete={handleReadingComplete}
            gradeLevel={currentReadingGrade || 1}
            // key ensures component remount between attempts or grade changes
            key={`reading-${currentReadingGrade}-${currentAttemptNumber}`}
            attemptNumber={currentAttemptNumber}
            preMessageText={preMessageText}
            fallbackActive={fallbackActive}
            onFallbackDone={handleFallbackDone}
          />
        );
      case SCREENS.RESULTS:
        return <ResultsScreen results={results} selectedGrade={selectedGrade} alphabetResults={alphabetResults} />;
      default:
        return <HomeScreen onNext={handleStart} />;
    }
  };

  return (
    <div className="mobile-container">
      {/* <NavigationBar /> */}
      <div className="page-content">
        {renderScreen()}
      </div>
      <Footer />
    </div>
  );
}

export default App;
